/**
 * The state of a CRM list page (spec-63 E2, design §5.5): the register and the views of the object,
 * the list the URL asks for, the options behind the filter controls, and the writers that change
 * the URL. Contacten and Organisaties differ only in `object`.
 *
 * The URL is the state (`view`, `q`, `f`, `sort`, `cols`, `page`); this hook reads it through
 * `urlState` and keeps the previous rows on screen while the next ones load. It does not search
 * until the URL is in the form it can tell is right: a last used view, an old `label_id` link, a
 * view that is gone, a corrupt filter and the parts of a filter that are plainly wrong (an operator
 * or a value the register does not have, an id that is no uuid) are first written back as a clean URL.
 *
 * What it can only see once a list has loaded is the exception. A label, category, list, team member,
 * organisation or person is known to be gone when the list or the answer of `GET /refs` says so; until
 * then its uuid is kept (that is all the server checks of an id: it is no error to name a deleted one).
 * A link to something deleted therefore searches once with it and again, with one message, when it is
 * dropped: the end state is right, but the first answer may be an empty list for a moment.
 */
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from '@/lib/toast'
import { autoSort } from '../filters/quickConditions'
import { useFilterOptions, type FilterOptions } from '../filters/useFilterOptions'
import { createView, getRegister, listViews, searchRecords, updateView } from '../query/api'
import { fitsInUrl } from '../query/codec'
import type { Condition, CrmObject, Filter, QueryBody, Register, SearchBody, SearchPage, ViewDef, ViewsResponse, Visibility } from '../query/types'
import {
  colsParam,
  DEFAULT_VIEW,
  effectiveState,
  filterParam,
  isBareUrl,
  legacyFilter,
  readListUrl,
  sortParam,
  viewSort,
  withListChanges,
  type EffectiveState,
  type ListChanges,
  type ListUrl,
} from '../query/urlState'
import { sanitizeFilter, type KnownIds } from '../query/validate'
import { viewSearch } from '../views/tabViews'

/** Rows per page (ED-38). */
export const PAGE_SIZE = 50

const UNKNOWN_VIEW = 'Deze weergave bestaat niet meer.'
const DROPPED_FROM_LINK = 'Een deel van het filter in deze link bestaat niet meer en is weggelaten.'
const DROPPED_FROM_VIEW = 'Een deel van het filter van deze weergave bestaat niet meer en is weggelaten.'
const TOO_BIG = 'Dit filter is te groot om in een link op te nemen. Haal voorwaarden of waarden weg.'
const VIEWS_NOT_RELOADED = 'Kon de weergaven niet vernieuwen.'

// ---- The last used view ----------------------------------------------------------------------

const rememberedKey = (object: CrmObject): string => `vonk.crm.${object}.weergave`

function readRemembered(object: CrmObject): string | null {
  try {
    return localStorage.getItem(rememberedKey(object))
  } catch {
    return null // storage blocked (private window, site data cleared): the list simply opens on the default view
  }
}

function writeRemembered(object: CrmObject, key: string): void {
  try {
    localStorage.setItem(rememberedKey(object), key)
  } catch {
    // The last used view is a convenience only.
  }
}

// ---- Writing the URL back --------------------------------------------------------------------

/** A URL that is rewritten before the list is searched, and the one message that goes with it. */
interface Rewrite {
  params: URLSearchParams
  message?: string
  /** It opens the last used view: only the first visit of the page does that. */
  restore?: boolean
}

interface RewriteInput {
  params: URLSearchParams
  url: ListUrl
  register: Register
  allViews: ViewDef[]
  /** The open view; null when the URL names one that does not exist. */
  view: ViewDef | null
  defaultView: ViewDef
  state: EffectiveState | null
  known: KnownIds
  first: boolean
  remembered: () => string | null
}

/**
 * What is wrong with the URL, in this order, one thing at a time (the rewritten URL is read again):
 * a bare URL on the first visit opens the last used view; the E1 parameters become a filter; a
 * view that is gone is left; and the parts of a filter that no longer exist are dropped, for a
 * link as well as for a saved view that names a deleted label (the view then shows as changed, so
 * it can be saved without them). `null` when the URL is as it should be.
 */
function planRewrite({ params, url, register, allViews, view, defaultView, state, known, first, remembered }: RewriteInput): Rewrite | null {
  let plan: Rewrite | null = null
  if (first && isBareUrl(params)) {
    // Only the very first visit: a later bare URL is the "Alle contacten" tab, which must stay open.
    const key = remembered()
    if (key && key !== DEFAULT_VIEW && allViews.some((candidate) => candidate.key === key)) {
      plan = { params: withListChanges(params, { view: key }), restore: true }
    }
  }
  if (!plan) {
    const legacy = legacyFilter(params, url.filter ?? defaultView.query)
    // The same list in the new form: the page of the old link stays, with its sort and search term.
    if (legacy) plan = { params: withListChanges(params, { f: filterParam(legacy, view ?? defaultView), page: params.get('page') }) }
  }
  if (!plan && !view) plan = { params: withListChanges(params, { view: null }), message: UNKNOWN_VIEW }
  if (!plan && view && state) {
    const { filter, dropped } = sanitizeFilter(state.filter, register, known)
    if (dropped > 0 || url.filterBroken) {
      // The parts came from the link, or, without a filter in the link, from the saved view itself (a label that was deleted since).
      const fromLink = url.filter !== null || url.filterBroken
      plan = { params: withListChanges(params, { f: filterParam(filter, view) }), message: fromLink ? DROPPED_FROM_LINK : DROPPED_FROM_VIEW }
    }
  }
  // A rewrite that changes nothing would keep the page waiting for a URL that never comes.
  return plan && plan.params.toString() !== params.toString() ? plan : null
}

// ---- The hook --------------------------------------------------------------------------------

interface Meta {
  object: CrmObject
  register: Register
  views: ViewsResponse
}

interface Answer<T> {
  /** The search this answers (`searchKey`). */
  key: string
  data: SearchPage<T> | null
  failed: boolean
}

/** What the writers read when they are called: the page as of the last render. */
interface Latest {
  params: URLSearchParams
  setParams: (next: URLSearchParams, options?: { replace?: boolean }) => void
  url: ListUrl
  state: EffectiveState | null
  view: ViewDef | null
  register: Register | null
  metaFailed: boolean
}

export interface RecordList<T> {
  register: Register | null
  views: ViewsResponse | null
  /** The open view; null while the views load, or when the URL names a view that is gone. */
  view: ViewDef | null
  /** The filter, sort, columns, search term and page the list shows; null until the register and the open view are known. */
  state: EffectiveState | null
  /** The filter with the search term: what bulk actions, export and counting work on. */
  query: QueryBody | null
  data: SearchPage<T> | null
  /** Nothing to show yet: the first page, the register, or the last page after a page past the end. */
  loading: boolean
  /** Rows are on screen and the next ones are on their way. */
  refreshing: boolean
  failed: boolean
  params: URLSearchParams
  options: FilterOptions
  /** Stable: the search field restarts its pause when this changes. */
  setQ: (q: string) => void
  setFilter: (next: Filter) => void
  setSort: (next: string) => void
  setColumns: (next: string[]) => void
  setPage: (page: number) => void
  /** Back to the view: filter, sort, columns and page go; the search term stays. */
  reset: () => void
  /** Overwrites the open saved view with the filter, columns and sort on screen. Rejects with the server's message. */
  saveView: () => Promise<void>
  /** Makes a new view of what is on screen and opens it. Rejects with the server's message (a name that is taken). */
  saveAsNew: (input: { name: string; visibility: Visibility }) => Promise<void>
  /** Searches again, and loads the register and views again when they failed. */
  reload: () => void
  /** Reads the views again (a change in "Alle weergaven"); false when that failed, after telling why. */
  reloadViews: () => Promise<boolean>
  /** Opens a view by its key with the search term kept, as a tab does; stable, and it reads the URL as it is when called. */
  openView: (key: string) => void
}

/** What a page can add to the list it shows (the members of a list, spec-63 E5). */
export interface RecordListOptions {
  pageSize?: number
  /** Quick conditions that always hold and are not part of the URL, the views or the active chips. */
  locked?: readonly Condition[]
  /** Fields that go into every search body next to the query (`list_context`, `member_sources`). */
  extraBody?: Pick<SearchBody, 'list_context' | 'member_sources'>
  /** Changes when the rows must be fetched again (the page around it changed the list). */
  reloadToken?: number
  /** Sort keys that exist on this page only and are allowed in the URL (Toegevoegd on a list's members). */
  extraSortKeys?: readonly string[]
  /** `false`: the page neither opens nor remembers a last used view (a page inside another page). */
  rememberView?: boolean
}

/** The filter with the locked conditions added to its quick filters. */
export function withLocked<F extends Filter>(filter: F, locked: readonly Condition[] | undefined): F {
  return locked && locked.length > 0 ? { ...filter, quick: [...filter.quick, ...locked] } : filter
}

export function useRecordList<T>(object: CrmObject, options: RecordListOptions = {}): RecordList<T> {
  const pageSize = options.pageSize ?? PAGE_SIZE
  const remember = options.rememberView !== false
  const lockedKey = JSON.stringify(options.locked ?? [])
  const extraBody = options.extraBody ?? {}
  const reloadToken = options.reloadToken ?? 0
  const [params, setParams] = useSearchParams()
  const url = useMemo(() => readListUrl(params), [params])

  // The register and the views of the object, together: neither is any use without the other.
  const [meta, setMeta] = useState<Meta | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [failedAttempt, setFailedAttempt] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    Promise.all([getRegister(object), listViews(object)]).then(
      ([register, views]) => {
        if (!cancelled) setMeta({ object, register, views })
      },
      () => {
        if (!cancelled) setFailedAttempt(attempt)
      },
    )
    return () => {
      cancelled = true
    }
  }, [object, attempt])
  const loaded = meta !== null && meta.object === object ? meta : null
  const register = loaded?.register ?? null
  const views = loaded?.views ?? null
  const metaFailed = loaded === null && failedAttempt === attempt

  const allViews = useMemo(() => (views ? [...views.system, ...views.views] : []), [views])
  const currentKey = url.view ?? DEFAULT_VIEW
  const view = useMemo(() => allViews.find((candidate) => candidate.key === currentKey) ?? null, [allViews, currentKey])
  const defaultView = useMemo(
    () => allViews.find((candidate) => candidate.key === DEFAULT_VIEW) ?? views?.system[0] ?? null,
    [allViews, views],
  )
  const extraSortKey = (options.extraSortKeys ?? []).join(',')
  const state = useMemo(
    () => (register && view ? effectiveState(url, view, register, extraSortKey ? extraSortKey.split(',') : []) : null),
    [url, view, register, extraSortKey],
  )
  const filterOptions = useFilterOptions(register, state?.filter ?? null)
  const { known } = filterOptions

  // The URL as it should be before the first search. `phase` is 'first' until the views are in and
  // the first visit has had its say about the last used view.
  const [phase, setPhase] = useState<'first' | 'done'>('first')
  const rewrite = useMemo(
    () =>
      register && defaultView
        ? planRewrite({
            params,
            url,
            register,
            allViews,
            view,
            defaultView,
            state,
            known,
            first: phase === 'first',
            remembered: () => (remember ? readRemembered(object) : null),
          })
        : null,
    [params, url, register, allViews, view, defaultView, state, known, phase, object, remember],
  )
  // Opening the last used view is not done until its URL has landed, or the page would show the
  // default view in between; every other rewrite stays true until its own URL lands.
  if (phase === 'first' && loaded !== null && !rewrite?.restore) setPhase('done')

  // One message per kind for what the user opens, not one per pass of the rewrites; their own next change starts afresh.
  const told = useRef(new Set<string>())
  useEffect(() => {
    if (!rewrite) return
    if (rewrite.message && !told.current.has(rewrite.message)) {
      told.current.add(rewrite.message)
      toast.info(rewrite.message)
    }
    setParams(rewrite.params, { replace: true })
  }, [rewrite, setParams])

  const viewKey = view?.key
  const urlIsFinal = rewrite === null
  useEffect(() => {
    if (remember && phase === 'done' && urlIsFinal && viewKey) writeRemembered(object, viewKey)
  }, [remember, phase, urlIsFinal, viewKey, object])

  // ---- Searching ----
  const query = useMemo<QueryBody | null>(() => {
    if (!state) return null
    const locked = JSON.parse(lockedKey) as Condition[]
    return { ...withLocked(state.filter, locked), ...(state.q ? { q: state.q } : {}) }
  }, [state, lockedKey])
  const [reloads, setReloads] = useState(0)
  // Everything that decides the rows, as one string: a change of columns is no new search.
  const searchKey = urlIsFinal && state && query ? JSON.stringify([query, state.sort, state.page, reloads + reloadToken, extraBody]) : null
  const [answer, setAnswer] = useState<Answer<T> | null>(null)
  useEffect(() => {
    if (searchKey === null) return
    const [body, sort, page, , extra] = JSON.parse(searchKey) as [QueryBody, string, number, number, Partial<SearchBody>]
    // A newer search wins: the cleanup of the previous effect ignores a late answer to the older one.
    let cancelled = false
    searchRecords<T>(object, { query: body, sort, page, page_size: pageSize, ...extra }).then(
      (found) => {
        if (!cancelled) setAnswer({ key: searchKey, data: found, failed: false })
      },
      () => {
        if (!cancelled) setAnswer({ key: searchKey, data: null, failed: true })
      },
    )
    return () => {
      cancelled = true
    }
  }, [object, pageSize, searchKey])

  const settled = answer !== null && answer.key === searchKey
  const lastPageOf = (found: SearchPage<T>): number => Math.max(1, Math.ceil(found.total / pageSize))
  // A page past the last one (an old link, a list that shrank) answers with no rows and the true total.
  const pastEnd = answer?.data ? answer.data.page > lastPageOf(answer.data) : false
  const data = answer?.data && !pastEnd ? answer.data : null
  const failed = metaFailed || (answer !== null && answer.key === searchKey && answer.failed)

  // ---- Writing the URL ----
  const latest = useRef<Latest | null>(null)
  useEffect(() => {
    latest.current = { params, setParams, url, state, view, register, metaFailed }
  })
  // The writers are stable and outlive the page in a toast's undo: once it is gone, they must not navigate back to the list.
  useEffect(
    () => () => {
      latest.current = null
    },
    [],
  )

  const apply = useCallback((changes: ListChanges, replace: boolean) => {
    const current = latest.current
    if (current) current.setParams(withListChanges(current.params, changes), { replace })
  }, [])
  /** A change the user made: what the rewrites told them about the old URL is old news. */
  const write = useCallback(
    (changes: ListChanges, replace = false) => {
      told.current.clear()
      apply(changes, replace)
    },
    [apply],
  )

  const lastPage = answer?.data ? lastPageOf(answer.data) : 1
  const goToLastPage = settled && pastEnd
  useEffect(() => {
    if (goToLastPage) apply({ page: lastPage > 1 ? String(lastPage) : null }, true)
  }, [goToLastPage, lastPage, apply])

  const setQ = useCallback((q: string) => write({ q: q || null }, true), [write])
  const setPage = useCallback((page: number) => write({ page: page > 1 ? String(page) : null }), [write])
  const setSort = useCallback(
    (next: string) => {
      const current = latest.current
      if (current?.view && current.register) write({ sort: sortParam(next, current.view, current.register) })
    },
    [write],
  )
  const setColumns = useCallback(
    (next: string[]) => {
      const current = latest.current
      // The columns decide nothing about the rows: the page stays where it is.
      if (current?.view && current.register) write({ cols: colsParam(next, current.view, current.register), page: current.params.get('page') })
    },
    [write],
  )
  const setFilter = useCallback(
    (next: Filter) => {
      const current = latest.current
      if (!current?.state || !current.view || !current.register) return
      // A link past this size would read as broken: refuse it instead of writing it.
      if (!fitsInUrl(next)) {
        toast.error(TOO_BIG)
        return
      }
      const sort = autoSort(current.state.filter, next, current.url.sort, viewSort(current.view, current.register))
      write({ f: filterParam(next, current.view), ...(sort ? { sort } : {}) })
    },
    [write],
  )
  const reset = useCallback(() => write({ f: null, sort: null, cols: null }), [write])

  // ---- Views ----
  /** Reads the views again; `ensure` is a view the server has just made, which a list read before it may lack. */
  const refreshViews = useCallback(
    async (ensure?: ViewDef): Promise<boolean> => {
      let fresh: ViewsResponse | null = null
      try {
        fresh = await listViews(object)
      } catch {
        toast.error(VIEWS_NOT_RELOADED)
      }
      // React Router navigates in a transition; the views go in one too, and React renders pending transitions together.
      // So new views never show ahead of a URL that was asked for first (deleting the open view opens the standard view,
      // then reads the views: the page must not see its own view gone and say "Deze weergave bestaat niet meer."), and a
      // view just made or brought back is in the list when the URL that opens it shows.
      startTransition(() => {
        setMeta((previous) => {
          if (!previous || previous.object !== object) return previous
          let next = fresh ?? previous.views
          if (ensure && !next.views.some((candidate) => candidate.key === ensure.key)) next = { ...next, views: [...next.views, ensure] }
          return { ...previous, views: next }
        })
      })
      return fresh !== null
    },
    [object],
  )
  const reloadViews = useCallback(() => refreshViews(), [refreshViews])

  // An undo in "Alle weergaven" opens a view seconds after it was asked for: what the URL says then counts.
  const openView = useCallback((key: string) => {
    const now = latest.current
    if (now) now.setParams(viewSearch(now.params, key))
  }, [])

  const saveView = useCallback(async () => {
    const current = latest.current
    if (!current?.view?.id || !current.state) return
    await updateView(current.view.id, { query: current.state.filter, columns: current.state.columns, sort: current.state.sort })
    // The view on screen is the old one until the new one is in: clearing the URL first would flash it.
    if (await refreshViews()) write({ f: null, sort: null, cols: null })
  }, [refreshViews, write])

  const saveAsNew = useCallback(
    async (input: { name: string; visibility: Visibility }) => {
      const current = latest.current
      if (!current?.state) return
      const created = await createView({
        object_type: object,
        name: input.name,
        visibility: input.visibility,
        query: current.state.filter,
        columns: current.state.columns,
        sort: current.state.sort,
      })
      await refreshViews(created)
      openView(created.key)
    },
    [object, refreshViews, openView],
  )

  const reload = useCallback(() => {
    setReloads((count) => count + 1)
    if (latest.current?.metaFailed) setAttempt((count) => count + 1)
  }, [])

  return {
    register,
    views,
    view,
    state,
    query,
    data,
    loading: !failed && data === null,
    refreshing: data !== null && !settled,
    failed,
    params,
    options: filterOptions,
    setQ,
    setFilter,
    setSort,
    setColumns,
    setPage,
    reset,
    saveView,
    saveAsNew,
    reload,
    reloadViews,
    openView,
  }
}
